import { useCallback, useEffect, useRef, useState } from 'react';

import { Cluster, MarkerClusterer } from '@googlemaps/markerclusterer';

import { getCluster } from '../utils/cluster';
import { ClusterOptions, IdType } from '../../types';
import { getDataForMarker } from '../utils/markers-to-marker-data';

type UseClustersProps = {
	map: google.maps.Map | null;
	markerInstanceMap: Map<IdType, google.maps.marker.AdvancedMarkerElement>;
	infoWindow: google.maps.InfoWindow;
	clusterOptions: ClusterOptions;
};

export type UseClustersReturn = {
	markerClusterer: MarkerClusterer | undefined;
	updateMarkerInCluster: (marker: google.maps.marker.AdvancedMarkerElement) => void;
};

const NUMBER_OF_DIGITS = 4;
export const CLUSTER_ID = 'cluster_id';

export const getPositionId = (position: google.maps.LatLng) => {
	const lat = position.lat().toFixed(NUMBER_OF_DIGITS);
	const lng = position.lng().toFixed(NUMBER_OF_DIGITS);

	return `${lat}_${lng}`;
};

export const useClusters = ({ map, markerInstanceMap, infoWindow, clusterOptions }: UseClustersProps) => {
	const [markerClusterer, setMarkerClusterer] = useState<MarkerClusterer>();
	const previousMarkersCount = useRef(0);
	const disposeClusterRef = useRef<() => void>();
	let onClusteringEndListener: google.maps.MapsEventListener;

	const onClusteringEnd = (_cluster: Cluster) => {
		if (infoWindow.isOpen) {
			const infoWindowClusterId = infoWindow.get(CLUSTER_ID);
			// @ts-ignore
			const clusterWithInfoWindow = _cluster.clusters.find(innerCluster => {
				const innerClusterId = getPositionId(innerCluster.position);
				return innerClusterId === infoWindowClusterId;
			});

			if (clusterWithInfoWindow && clusterWithInfoWindow.markers.length <= 1) {
				infoWindow.close();
				infoWindow.set(CLUSTER_ID, null);
			}
		}
	};

	useEffect(() => {
		if (clusterOptions.cluster && map) {
			disposeClusterRef.current?.();
			markerClusterer?.clearMarkers();
			setMarkerClusterer(undefined);
			const { clusterer: clusterInstance, dispose } = getCluster(
				map,
				markerInstanceMap,
				infoWindow,
				clusterOptions
			);
			disposeClusterRef.current = dispose;
			previousMarkersCount.current = markerInstanceMap.size;
			onClusteringEndListener = google.maps.event.addListener(clusterInstance, 'clusteringend', onClusteringEnd);
			setMarkerClusterer(clusterInstance);
		}
	}, [markerInstanceMap]);

	useEffect(() => {
		return () => {
			disposeClusterRef.current?.();
			infoWindow.set(CLUSTER_ID, null);
			onClusteringEndListener?.remove();
		};
	}, []);

	const updateMarkerInCluster = useCallback(
		(marker: google.maps.marker.AdvancedMarkerElement) => {
			if (!clusterOptions.cluster || !markerClusterer) {
				return;
			}

			const markerData = getDataForMarker(marker);
			if (!markerData?.shouldCluster) {
				return;
			}

			// @ts-ignore — markers is protected but accessible at runtime
			const isTracked = markerClusterer.markers.includes(marker);
			if (!isTracked) {
				markerClusterer.addMarker(marker, true);
			}

			markerClusterer.render();

			// After render, check if we need to close the cluster tooltip
			const clusterTooltipId = infoWindow.get(CLUSTER_ID);
			if (clusterTooltipId) {
				// @ts-ignore - clusters is protected but we need to access it
				const clusters = markerClusterer.clusters as Cluster[] | undefined;
				if (clusters) {
					const clusterWithTooltip = clusters.find(cluster => {
						const clusterId = getPositionId(cluster.position);
						return clusterId === clusterTooltipId;
					});

					if (!clusterWithTooltip || (clusterWithTooltip.markers?.length ?? 0) <= 1) {
						infoWindow.close();
						infoWindow.set(CLUSTER_ID, null);
					}
				}
			}
		},
		[clusterOptions.cluster, markerClusterer, infoWindow]
	);

	return {
		markerClusterer,
		updateMarkerInCluster
	};
};
